import {
  ArrowRightLeft,
  CalendarClock,
  CircleAlert,
  Euro,
  Handshake,
  Pencil,
  User,
} from "lucide-react";
import { useLocaleState, useTranslate } from "ra-core";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { useMoneyFormat } from "@/hooks/use-money-format";
import { cn } from "@/lib/utils";

import { Chip } from "./components";
import { formatShortDate } from "./dates";
import {
  OPPORTUNITY_STAGES,
  opportunityStageLabelKey,
  opportunityTypeLabelKey,
} from "./labels";
import { useStageChange } from "./marketHooks";
import { canConvertToDeal, isOverdue } from "./opportunities";
import { QuickMenu } from "./QuickMenu";
import type { MarketOpportunity, OpportunityStage } from "./types";
import { riseStyle } from "./util";

const STAGE_DOT: Record<OpportunityStage, string> = {
  nueva: "bg-sky-500",
  en_contacto: "bg-amber-500",
  propuesta: "bg-violet-500",
  ganada: "bg-emerald-500",
  perdida: "bg-slate-400",
};

export const StageBadge = ({ stage }: { stage: OpportunityStage }) => {
  const translate = useTranslate();
  return (
    <span className="inline-flex h-6 shrink-0 items-center gap-1.5 rounded-full bg-muted px-2.5 text-xs font-semibold">
      <span
        aria-hidden
        className={cn("size-1.5 rounded-full", STAGE_DOT[stage])}
      />
      <span className="sr-only">
        {translate("market.opportunity_stage.label")}:{" "}
      </span>
      {translate(opportunityStageLabelKey(stage))}
    </span>
  );
};

interface OpportunityCardProps {
  opportunity: MarketOpportunity;
  today: string;
  onEdit: (opportunity: MarketOpportunity) => void;
  onConvert: (opportunity: MarketOpportunity) => void;
  /** Kanban card: tighter, the stage is the column. */
  compact?: boolean;
  index: number;
}

export const OpportunityCard = ({
  opportunity,
  today,
  onEdit,
  onConvert,
  compact,
  index,
}: OpportunityCardProps) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const { formatMoney } = useMoneyFormat();
  const changeStage = useStageChange();
  const overdue = isOverdue(opportunity, today);
  const titleId = `mi-opportunity-${opportunity.id}`;
  const hasValue =
    opportunity.estimated_value != null &&
    `${opportunity.estimated_value}` !== "";

  return (
    <article
      aria-labelledby={titleId}
      data-interactive
      data-overdue={overdue || undefined}
      className={cn(
        "mi-card flex min-w-0 flex-col gap-2.5",
        compact ? "p-3.5" : "mi-rise p-4",
      )}
      style={compact ? undefined : riseStyle(index)}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1 space-y-1.5">
          <h3
            id={titleId}
            className={cn(
              "leading-snug font-semibold wrap-anywhere",
              compact ? "text-[15px]" : "text-[17px]",
            )}
          >
            {opportunity.name}
          </h3>
          <div className="mi-chip-row">
            <span className="inline-flex h-6 shrink-0 items-center rounded-full bg-primary/10 px-2.5 text-xs font-semibold text-primary dark:bg-primary/15">
              {translate(opportunityTypeLabelKey(opportunity.type))}
            </span>
            {opportunity.sector ? <Chip>{opportunity.sector}</Chip> : null}
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="-mt-1 -mr-1 shrink-0 rounded-full text-muted-foreground"
          onClick={() => onEdit(opportunity)}
          aria-label={`${translate("market.opportunities.edit")}: ${opportunity.name}`}
        >
          <Pencil className="size-4" />
        </Button>
      </div>

      {hasValue || opportunity.contact ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          {hasValue ? (
            <span className="inline-flex items-center gap-1 font-semibold tabular-nums">
              <Euro aria-hidden className="size-3.5 text-primary" />
              <span className="sr-only">
                {translate("market.opportunities.estimated_value")}:{" "}
              </span>
              {formatMoney(Number(opportunity.estimated_value))}
            </span>
          ) : null}
          {opportunity.contact ? (
            <span className="inline-flex min-w-0 items-center gap-1 text-muted-foreground">
              <User aria-hidden className="size-3.5 shrink-0" />
              <span className="sr-only">
                {translate("market.opportunities.contact")}:{" "}
              </span>
              <span className="truncate">{opportunity.contact}</span>
            </span>
          ) : null}
        </div>
      ) : null}

      <NextStep opportunity={opportunity} overdue={overdue} locale={locale} />

      <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
        <QuickMenu<OpportunityStage>
          label={`${translate("market.opportunity_stage.change")}: ${opportunity.name}`}
          value={opportunity.stage}
          options={OPPORTUNITY_STAGES.map((value) => ({
            value,
            label: translate(opportunityStageLabelKey(value)),
          }))}
          onChange={(value) => changeStage(opportunity, value)}
          trigger={<StageBadge stage={opportunity.stage} />}
        />
        {opportunity.deal_id != null ? (
          <Link
            to={`/deals/${encodeURIComponent(String(opportunity.deal_id))}/show`}
            className="ml-auto inline-flex h-8 items-center gap-1 rounded-full px-2 text-xs font-semibold text-primary hover:underline"
            aria-label={`${translate("market.opportunities.deal_link")}: ${opportunity.name}`}
          >
            <Handshake aria-hidden className="size-3.5" />
            {translate("market.opportunities.deal_link")}
          </Link>
        ) : canConvertToDeal(opportunity) ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onConvert(opportunity)}
            className="ml-auto h-8 rounded-full px-2.5 text-xs font-semibold text-primary"
          >
            <ArrowRightLeft aria-hidden className="size-3.5" />
            {translate("market.opportunities.convert.action")}
          </Button>
        ) : null}
      </div>
    </article>
  );
};

const NextStep = ({
  opportunity,
  overdue,
  locale,
}: {
  opportunity: MarketOpportunity;
  overdue: boolean;
  locale: string;
}) => {
  const translate = useTranslate();
  if (!opportunity.next_step && !opportunity.next_step_date) return null;
  return (
    <div
      className={cn(
        "flex min-w-0 items-start gap-2 rounded-xl px-2.5 py-2 text-sm",
        overdue
          ? "bg-red-500/10 text-red-800 ring-1 ring-red-500/25 ring-inset dark:bg-red-400/12 dark:text-red-200 dark:ring-red-400/30"
          : "bg-muted/70",
      )}
    >
      {overdue ? (
        <CircleAlert aria-hidden className="mt-0.5 size-4 shrink-0" />
      ) : (
        <CalendarClock
          aria-hidden
          className="mt-0.5 size-4 shrink-0 text-muted-foreground"
        />
      )}
      <div className="min-w-0 flex-1">
        <p className="sr-only">{translate("market.opportunities.next_step")}</p>
        {opportunity.next_step ? (
          <p className="font-medium wrap-anywhere">{opportunity.next_step}</p>
        ) : null}
        {opportunity.next_step_date ? (
          <p
            className={cn(
              "text-xs",
              overdue ? "font-semibold" : "text-muted-foreground",
            )}
          >
            <time dateTime={opportunity.next_step_date}>
              {translate("market.opportunities.due", {
                date: formatShortDate(opportunity.next_step_date, locale),
              })}
            </time>
            {overdue ? ` · ${translate("market.opportunities.overdue")}` : ""}
          </p>
        ) : null}
      </div>
    </div>
  );
};
